import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatTL } from "@/lib/api";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, Bus } from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, count } = useCart();
  const { settings } = useSettings();
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const navigate = useNavigate();

  const discount = appliedCoupon ? Math.round((subtotal * appliedCoupon.discount_percent) / 100) : 0;
  const busFee = subtotal === 0 ? 0 : (settings.bus_fee || 400);
  const total = Math.max(0, subtotal - discount + busFee);

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      const { data } = await api.get(`/coupons/${coupon.toUpperCase()}`);
      if (subtotal < (data.min_amount || 0)) {
        toast.error(`Kupon için minimum ${formatTL(data.min_amount)} tutar gerekir`);
        return;
      }
      setAppliedCoupon(data);
      toast.success(`%${data.discount_percent} indirim uygulandı`);
    } catch {
      toast.error("Geçersiz kupon kodu");
    }
  };

  if (count === 0) {
    return (
      <div data-testid="cart-empty" className="container-max px-4 md:px-8 section-pad text-center">
        <ShoppingBag size={48} className="mx-auto text-[#0077B6] mb-5" />
        <h1 className="font-display text-3xl font-extrabold mb-3">Sepetiniz Boş</h1>
        <p className="text-slate-600 mb-6">Taze midyelerimizi keşfedin.</p>
        <Link to="/urunler" className="btn-primary">Alışverişe Başla <ArrowRight size={16} /></Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="container-max px-4 md:px-8 section-pad">
      <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mb-8">Sepetim</h1>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-3">
          {items.map((it) => (
            <div key={it.product_id} className="bg-white rounded-2xl border border-slate-100 p-4 flex gap-4">
              <img src={it.image} alt={it.name} className="w-24 h-24 rounded-xl object-cover" />
              <div className="flex-1">
                <Link to={`/urun/${it.slug}`} className="font-display font-bold hover:text-[#0077B6]">{it.name}</Link>
                <div className="text-[#0077B6] font-bold mt-1">{formatTL(it.price)}</div>
                <div className="flex items-center gap-2 mt-3">
                  <div className="inline-flex items-center border-2 border-slate-200 rounded-full">
                    <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-1.5"><Minus size={13} /></button>
                    <span className="w-8 text-center font-bold text-sm">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-1.5"><Plus size={13} /></button>
                  </div>
                  <button onClick={() => removeItem(it.product_id)} className="ml-auto text-rose-600 hover:text-rose-700 p-2" data-testid={`remove-${it.slug}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="text-right font-display font-extrabold text-lg">
                {formatTL(it.price * it.quantity)}
              </div>
            </div>
          ))}
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 sticky top-24">
            <h2 className="font-display text-xl font-extrabold mb-5">Sipariş Özeti</h2>

            <div className="flex gap-2 mb-5">
              <input
                data-testid="coupon-input"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="Kupon kodu"
                className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:border-[#0077B6]"
              />
              <button data-testid="coupon-apply" onClick={applyCoupon} className="rounded-full bg-[#0A192F] text-white px-4 text-sm font-semibold">Uygula</button>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Ara toplam</span><span className="font-semibold">{formatTL(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-emerald-700"><span>İndirim ({appliedCoupon.code})</span><span>-{formatTL(discount)}</span></div>}
              <div className="flex justify-between"><span className="text-slate-600 flex items-center gap-1.5"><Bus size={14}/> Otobüs ücreti</span><span className="font-semibold">{formatTL(busFee)}</span></div>
              <div className="border-t border-slate-200 pt-3 mt-3 flex justify-between font-display text-xl font-extrabold">
                <span>Toplam</span><span data-testid="cart-total" className="text-[#0077B6]">{formatTL(total)}</span>
              </div>
            </div>

            <button
              data-testid="checkout-cta"
              onClick={() => navigate("/odeme", { state: { coupon: appliedCoupon?.code } })}
              className="btn-primary w-full mt-6"
            >
              Ödemeye Geç <ArrowRight size={16} />
            </button>
            <Link to="/urunler" className="block text-center text-sm text-slate-500 mt-3 hover:text-[#0077B6]">Alışverişe Devam Et</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
